import { api } from './api.js';
import type { NoteUser } from '../components/noteSharing.js';

/** What GET /providers gives a non-admin for each colleague (ehr#60 name cards). */
interface ProviderCard {
  id: string;
  first?: string;
  last?: string;
  display?: string;
  email?: string;
}

/**
 * ehr#571 - the practice's people for the note editors' names (Signed in as, Signatures, the
 * reviewer). GET /settings/users is for admins and managers only, so a clinician (or an admin
 * viewing the EHR as one) got none and every name read "—". They get GET /providers' name
 * cards instead: names only (no role or supervisor), which is all those lines show.
 *
 * ehr#623: any place that only names people (a client's owner in the chart header and the
 * Contacts list, note authors, assignees) reads them from here, not from /settings/users.
 */
export async function fetchNoteUsers(): Promise<NoteUser[]> {
  try {
    return await api.get<NoteUser[]>('/settings/users');
  } catch {
    try {
      const r = await api.get<{ providers: ProviderCard[] }>('/providers');
      return (r.providers ?? []).map((p) => ({
        id: p.id,
        firstName: p.first?.trim() || null,
        lastName: p.last?.trim() || null,
        // the card has no email for a colleague; its display name is the name fallback
        email: p.email || p.display || '',
      }));
    } catch {
      return [];
    }
  }
}

/** ehr#623 - a person's name (first last, else email) by id; undefined when there is no such person. */
export function personLabel(
  users: Array<Pick<NoteUser, 'id' | 'firstName' | 'lastName' | 'email'>>,
  id: string | null | undefined,
): string | undefined {
  if (!id) return undefined;
  const u = users.find((x) => x.id === id);
  return u ? [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email : undefined;
}
